// src/pages/AdminHolidays.jsx
import React, { useEffect, useMemo, useState } from "react";
import apiClient from "../api";

// Simple holiday types
const TYPE_OPTIONS = [
  { value: "national", label: "National" },
  { value: "poya", label: "Poya" },
  { value: "festival", label: "Festival" },
  { value: "special", label: "Special" },
  { value: "other", label: "Other" },
];

// More color options
const COLOR_OPTIONS = [
  { value: "rose", label: "Rose" },
  { value: "red", label: "Red" },
  { value: "orange", label: "Orange" },
  { value: "amber", label: "Amber" },
  { value: "yellow", label: "Yellow" },
  { value: "lime", label: "Lime" },
  { value: "green", label: "Green" },
  { value: "emerald", label: "Emerald" },
  { value: "teal", label: "Teal" },
  { value: "cyan", label: "Cyan" },
  { value: "sky", label: "Sky" },
  { value: "blue", label: "Blue" },
  { value: "indigo", label: "Indigo" },
  { value: "violet", label: "Violet" },
  { value: "purple", label: "Purple" },
  { value: "pink", label: "Pink" },
  { value: "slate", label: "Slate" },
];

const PALETTES = {
  rose: { chipBg: "bg-rose-50", chipText: "text-rose-700", pillBg: "bg-rose-100", ring: "ring-rose-200", dot: "bg-rose-500" },
  red: { chipBg: "bg-red-50", chipText: "text-red-700", pillBg: "bg-red-100", ring: "ring-red-200", dot: "bg-red-500" },
  orange: { chipBg: "bg-orange-50", chipText: "text-orange-700", pillBg: "bg-orange-100", ring: "ring-orange-200", dot: "bg-orange-500" },
  amber: { chipBg: "bg-amber-50", chipText: "text-amber-800", pillBg: "bg-amber-100", ring: "ring-amber-200", dot: "bg-amber-500" },
  yellow: { chipBg: "bg-yellow-50", chipText: "text-yellow-800", pillBg: "bg-yellow-100", ring: "ring-yellow-200", dot: "bg-yellow-500" },
  lime: { chipBg: "bg-lime-50", chipText: "text-lime-800", pillBg: "bg-lime-100", ring: "ring-lime-200", dot: "bg-lime-500" },
  green: { chipBg: "bg-green-50", chipText: "text-green-700", pillBg: "bg-green-100", ring: "ring-green-200", dot: "bg-green-500" },
  emerald: { chipBg: "bg-emerald-50", chipText: "text-emerald-700", pillBg: "bg-emerald-100", ring: "ring-emerald-200", dot: "bg-emerald-500" },
  teal: { chipBg: "bg-teal-50", chipText: "text-teal-700", pillBg: "bg-teal-100", ring: "ring-teal-200", dot: "bg-teal-500" },
  cyan: { chipBg: "bg-cyan-50", chipText: "text-cyan-700", pillBg: "bg-cyan-100", ring: "ring-cyan-200", dot: "bg-cyan-500" },
  sky: { chipBg: "bg-sky-50", chipText: "text-sky-700", pillBg: "bg-sky-100", ring: "ring-sky-200", dot: "bg-sky-500" },
  blue: { chipBg: "bg-blue-50", chipText: "text-blue-700", pillBg: "bg-blue-100", ring: "ring-blue-200", dot: "bg-blue-500" },
  indigo: { chipBg: "bg-indigo-50", chipText: "text-indigo-700", pillBg: "bg-indigo-100", ring: "ring-indigo-200", dot: "bg-indigo-500" },
  violet: { chipBg: "bg-violet-50", chipText: "text-violet-700", pillBg: "bg-violet-100", ring: "ring-violet-200", dot: "bg-violet-500" },
  purple: { chipBg: "bg-purple-50", chipText: "text-purple-700", pillBg: "bg-purple-100", ring: "ring-purple-200", dot: "bg-purple-500" },
  pink: { chipBg: "bg-pink-50", chipText: "text-pink-700", pillBg: "bg-pink-100", ring: "ring-pink-200", dot: "bg-pink-500" },
  slate: { chipBg: "bg-slate-50", chipText: "text-slate-700", pillBg: "bg-slate-100", ring: "ring-slate-200", dot: "bg-slate-500" },
};

const TYPE_COLOR_MAP = {
  national: "rose",
  poya: "indigo",
  festival: "amber",
  special: "emerald",
  other: "slate",
};

const QUICK_TITLES = [
  "Poya Day",
  "Sinhala & Tamil New Year",
  "Vesak Full Moon Poya",
  "Poson Full Moon Poya",
  "Deepavali",
  "Christmas Day",
  "Id Ul-Fitr",
  "Id Ul-Alha",
  "Special Holiday",
];

const fmtDayMon = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
      })
    : "DD Mon";

const toInputDate = (value) => {
  if (!value) return "";
  return String(value).slice(0, 10);
};

const parseHolidayTime = (value) => {
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : Number.POSITIVE_INFINITY;
};

const sortHolidaysByDate = (list = []) =>
  [...list].sort((a, b) => {
    const dateDiff = parseHolidayTime(a.date) - parseHolidayTime(b.date);
    if (dateDiff !== 0) return dateDiff;
    return String(a.title || "").localeCompare(String(b.title || ""));
  });

const todayInput = () => new Date().toISOString().slice(0, 10);

const HolidayPreview = ({ title, date, colorKey }) => {
  const pal = PALETTES[colorKey] || PALETTES.rose;

  return (
    <div className={`inline-flex items-center gap-2 rounded-2xl px-4 py-3 ring-1 ${pal.ring} ${pal.chipBg}`}>
      <span className={`text-[11px] font-bold uppercase tracking-[0.12em] px-2 py-1 rounded-full ${pal.pillBg} ${pal.chipText}`}>
        {fmtDayMon(date)}
      </span>
      <span className={`text-sm font-bold ${pal.chipText}`}>
        {title || "Holiday name"}
      </span>
    </div>
  );
};

const AdminHolidays = () => {
  const token = localStorage.getItem("token");

  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [type, setType] = useState("national");
  const [colorKey, setColorKey] = useState("rose");
  const [isActive, setIsActive] = useState(true);

  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const sortedHolidays = useMemo(() => sortHolidaysByDate(holidays), [holidays]);

  const fetchHolidays = async () => {
    try {
      setFetching(true);
      const res = await apiClient.get("/holidays", {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      setHolidays(sortHolidaysByDate(Array.isArray(res.data) ? res.data : []));
    } catch (err) {
      console.error("Failed to fetch holidays", err);
      alert(err?.response?.data?.message || "Failed to fetch holidays");
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    if (token) fetchHolidays();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const resetForm = () => {
    setSavingId(null);
    setTitle("");
    setDate("");
    setType("national");
    setColorKey("rose");
    setIsActive(true);
  };

  const handleTypeChange = (value) => {
    setType(value);
    setColorKey(TYPE_COLOR_MAP[value] || "rose");
  };

  const handleSave = async () => {
    if (!title.trim() || !date) {
      alert("Please add holiday name and date.");
      return;
    }

    const body = {
      title: title.trim(),
      date,
      type,
      colorKey,
      isActive,
      link: "",
      ctaLabel: "",
      order: 0,
    };

    try {
      setLoading(true);

      if (savingId) {
        await apiClient.patch(`/holidays/${savingId}`, body, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });
      } else {
        await apiClient.post("/holidays", body, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });
      }

      await fetchHolidays();
      resetForm();
      alert(savingId ? "Holiday updated!" : "Holiday added!");
    } catch (err) {
      console.error("Save failed", err);
      alert(err?.response?.data?.message || "Failed to save holiday");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (holiday) => {
    setSavingId(holiday._id);
    setTitle(holiday.title || "");
    setDate(toInputDate(holiday.date));
    setType(holiday.type || "national");
    setColorKey(holiday.colorKey || "rose");
    setIsActive(Boolean(holiday.isActive));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    const ok = window.confirm("Delete this holiday?");
    if (!ok) return;

    try {
      setDeletingId(id);
      await apiClient.delete(`/holidays/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      setHolidays((prev) => prev.filter((h) => h._id !== id));
    } catch (err) {
      console.error("Delete failed", err);
      alert(err?.response?.data?.message || "Failed to delete holiday");
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleActive = async (holiday) => {
    const body = {
      title: holiday.title,
      date: toInputDate(holiday.date),
      type: holiday.type || "national",
      colorKey: holiday.colorKey || "rose",
      isActive: !holiday.isActive,
      link: holiday.link || "",
      ctaLabel: holiday.ctaLabel || "",
      order: Number(holiday.order) || 0,
    };

    try {
      await apiClient.patch(`/holidays/${holiday._id}`, body, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });

      setHolidays((prev) =>
        prev.map((h) =>
          h._id === holiday._id ? { ...h, isActive: !h.isActive } : h
        )
      );
    } catch (err) {
      console.error("Status update failed", err);
      alert(err?.response?.data?.message || "Failed to update status");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex flex-col gap-1">
          <h1 className="text-2xl font-extrabold text-gray-900">
            Manage Holidays
          </h1>
          <p className="text-sm text-gray-500">
            Add, edit, hide, or delete homepage holidays easily.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6 mb-8">
          {/* Form */}
          <div className="rounded-3xl border border-gray-200 bg-white shadow-sm p-5">
            <div className="flex items-center justify-between gap-3 mb-5">
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  {savingId ? "Edit Holiday" : "Add Holiday"}
                </h2>
                <p className="text-xs text-gray-500 mt-1">
                  Only name, date, type, color and active status are needed.
                </p>
              </div>

              {savingId && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-3 py-2 rounded-full border text-xs font-bold text-gray-600 hover:bg-gray-50"
                >
                  Cancel
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-xs font-bold text-gray-600 mb-1">
                  Holiday Name
                </label>
                <input
                  type="text"
                  placeholder="Eg: Id Ul-Alha"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm font-semibold outline-none focus:ring-2 focus:ring-red-100 focus:border-red-300"
                  list="holiday-title-list"
                />
                <datalist id="holiday-title-list">
                  {QUICK_TITLES.map((item) => (
                    <option key={item} value={item} />
                  ))}
                </datalist>

                <div className="mt-2 flex flex-wrap gap-2">
                  {QUICK_TITLES.slice(0, 6).map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => setTitle(item)}
                      className="px-3 py-1.5 rounded-full bg-gray-100 text-gray-700 text-[11px] font-bold hover:bg-gray-200"
                    >
                      {item}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-600 mb-1">
                  Date
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm font-semibold outline-none focus:ring-2 focus:ring-red-100 focus:border-red-300"
                />
                <button
                  type="button"
                  onClick={() => setDate(todayInput())}
                  className="mt-2 text-[11px] font-bold text-red-500 hover:text-red-600"
                >
                  Set today
                </button>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-600 mb-1">
                  Type
                </label>
                <select
                  value={type}
                  onChange={(e) => handleTypeChange(e.target.value)}
                  className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm font-semibold outline-none focus:ring-2 focus:ring-red-100 focus:border-red-300"
                >
                  {TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-xs font-bold text-gray-600 mb-2">
                  Color
                </label>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                  {COLOR_OPTIONS.map((color) => {
                    const pal = PALETTES[color.value] || PALETTES.rose;
                    const selected = colorKey === color.value;

                    return (
                      <button
                        key={color.value}
                        type="button"
                        onClick={() => setColorKey(color.value)}
                        className={`flex items-center gap-2 rounded-2xl border px-3 py-2 text-xs font-bold transition ${
                          selected
                            ? "border-gray-900 bg-gray-900 text-white"
                            : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        <span className={`h-3 w-3 rounded-full ${pal.dot}`} />
                        {color.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="flex items-center justify-between rounded-2xl border border-gray-200 px-4 py-3 cursor-pointer">
                  <div>
                    <p className="text-sm font-bold text-gray-900">
                      Show on website
                    </p>
                    <p className="text-xs text-gray-500">
                      Turn off to hide this holiday without deleting.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    className="h-5 w-5"
                  />
                </label>
              </div>
            </div>

            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={handleSave}
                disabled={loading}
                className="w-full sm:w-auto rounded-2xl bg-red-500 px-6 py-3 text-sm font-extrabold text-white shadow-sm hover:bg-red-600 disabled:opacity-60"
              >
                {loading
                  ? "Saving..."
                  : savingId
                  ? "Update Holiday"
                  : "Add Holiday"}
              </button>

              <button
                type="button"
                onClick={resetForm}
                className="w-full sm:w-auto rounded-2xl border border-gray-200 bg-white px-6 py-3 text-sm font-bold text-gray-700 hover:bg-gray-50"
              >
                Clear
              </button>
            </div>
          </div>

          {/* Preview */}
          <div className="rounded-3xl border border-gray-200 bg-white shadow-sm p-5">
            <h2 className="text-lg font-bold text-gray-900 mb-2">
              Live Preview
            </h2>
            <p className="text-xs text-gray-500 mb-5">
              This is how the holiday chip looks on homepage.
            </p>

            <HolidayPreview title={title} date={date} colorKey={colorKey} />

            <div className="mt-6 rounded-2xl bg-gray-50 border border-gray-100 p-4">
              <p className="text-xs font-bold text-gray-500 mb-1">
                Quick Summary
              </p>
              <p className="text-sm font-semibold text-gray-900">
                {title || "No holiday name"}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {date || "No date selected"} · {type} ·{" "}
                {isActive ? "Visible" : "Hidden"}
              </p>
            </div>
          </div>
        </div>

        {/* List */}
        <div className="rounded-3xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                Holiday List
              </h2>
              <p className="text-xs text-gray-500">
                Sorted by nearest date first.
              </p>
            </div>
            <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-bold text-gray-600">
              {sortedHolidays.length} total
            </span>
          </div>

          {fetching ? (
            <div className="p-6 text-sm font-semibold text-gray-500">
              Loading holidays...
            </div>
          ) : sortedHolidays.length === 0 ? (
            <div className="p-6 text-sm font-semibold text-gray-500">
              No holidays added yet.
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {sortedHolidays.map((holiday) => {
                const pal = PALETTES[holiday.colorKey] || PALETTES.rose;

                return (
                  <div
                    key={holiday._id}
                    className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <HolidayPreview
                          title={holiday.title}
                          date={holiday.date}
                          colorKey={holiday.colorKey}
                        />

                        <span
                          className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${
                            holiday.isActive
                              ? "bg-green-50 text-green-700"
                              : "bg-gray-100 text-gray-500"
                          }`}
                        >
                          {holiday.isActive ? "Visible" : "Hidden"}
                        </span>
                      </div>

                      <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                        <span className={`h-2 w-2 rounded-full ${pal.dot}`} />
                        <span>{holiday.type || "other"}</span>
                        <span>·</span>
                        <span>{holiday.colorKey || "rose"}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => handleToggleActive(holiday)}
                        className="rounded-xl border border-gray-200 px-3 py-2 text-xs font-bold text-gray-700 hover:bg-gray-50"
                      >
                        {holiday.isActive ? "Hide" : "Show"}
                      </button>

                      <button
                        type="button"
                        onClick={() => handleEdit(holiday)}
                        className="rounded-xl border border-gray-200 px-3 py-2 text-xs font-bold text-gray-700 hover:bg-gray-50"
                      >
                        Edit
                      </button>

                      <button
                        type="button"
                        onClick={() => handleDelete(holiday._id)}
                        disabled={deletingId === holiday._id}
                        className="rounded-xl bg-red-500 px-3 py-2 text-xs font-bold text-white hover:bg-red-600 disabled:opacity-60"
                      >
                        {deletingId === holiday._id ? "Deleting..." : "Delete"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminHolidays;
